import { deepCopy } from "./helpers";
import { LighthouseData, Sector, SectorColor } from "./lighthouseConstants";

/**
 * Extracted from lighthouseUtils.ts so the sector definition table's validation and the
 * sector editor delegate to the same functions instead of each carrying their own copy -
 * see sectorDraftValidation.ts, which is the other caller. Moved verbatim; do not change
 * the logic here without checking both callers.
 */
export const satisfiesIALA = (lighthouse: LighthouseData) => {
    if (!lighthouse) return false;

    for (let i = 0; i < lighthouse.sectors.length; i++) {
        const sector = lighthouse.sectors[i];
        const prevSector = lighthouse.sectors[i === 0 ? lighthouse.sectors.length - 1 : i - 1];
        const nextSector = lighthouse.sectors[(i + 1) % lighthouse.sectors.length];

        // If a sector is white, starboard must be green and portside must be red
        if (sector.color === SectorColor.WHITE) {
            if (prevSector.color !== SectorColor.GREEN || nextSector.color !== SectorColor.RED) {
                return false;
            }
        }
    }
    return true;
};

export const hasSameColorConsecutiveSectors = (sectors: Sector[]) => {
    if ([0, 1].includes(sectors.length)) return false;

    const sectorsSorted = deepCopy(sectors);
    sectorsSorted.sort((a: Sector, b: Sector) => (b.startAngle ?? 0) - (a.startAngle ?? 0));

    for (let i = 0; i < sectorsSorted.length; i++) {
        if (sectorsSorted[i].color === sectorsSorted[(i + 1) % sectorsSorted.length].color) return true;
    }

    return false;
};

/** A sector whose description starts with "Fra " (any case, leading whitespace ignored) is sector 1. */
export const isFraDescription = (description: string | null | undefined) =>
    /^fra\s/i.test(description?.trimStart() ?? "");

/** Explains the isFraDescription rule to users where sector descriptions are typed in a table. */
export const FRA_DESCRIPTION_HINT = "En beskrivelse som starter med «Fra …» gjør sektoren til sektor 1.";

/*
 * Sector ordering and numbering, moved from lighthouseUtils.ts (which re-exports them) so the
 * sector definition table can share the one rule without pulling in ArcGIS. Every listing, the
 * sector rose and the saved sektor_nr derive from shiftSectors.
 */
export const getFirstDarkSectorIndex = (lighthouseData: Pick<LighthouseData, "sectors"> | null | undefined) => {
    const sectorsSorted = deepCopy(lighthouseData?.sectors ?? []).sort(
        (a: Sector, b: Sector) => a.startAngle! - b.startAngle!
    );

    const lastSector = sectorsSorted[sectorsSorted.length - 1];
    let firstDarkSectorIndex;

    if (lastSector.color === SectorColor.DARK) {
        firstDarkSectorIndex = 0;
    } else {
        firstDarkSectorIndex = sectorsSorted.findIndex((sector: Sector) => sector.color === SectorColor.DARK);
        if (firstDarkSectorIndex === undefined || firstDarkSectorIndex === -1) return sectorsSorted;
    }

    const index = firstDarkSectorIndex % (lighthouseData?.sectors.length ?? 1);
    return index;
};

/**
 * The sectors in numbering order: from the first lit sector described "Fra ...", otherwise from
 * the first lit sector after the first dark sector, otherwise in array order.
 */
export const shiftSectors = (lighthouseData: Pick<LighthouseData, "sectors"> | null | undefined): Sector[] => {
    const shifted = shiftSectorsAfterFirstDark(lighthouseData);
    const fraIndex = shifted.findIndex(
        (sector) => sector.color !== SectorColor.DARK && isFraDescription(sector.description)
    );
    return fraIndex > 0 ? shifted.slice(fraIndex).concat(shifted.slice(0, fraIndex)) : shifted;
};

const shiftSectorsAfterFirstDark = (lighthouseData: Pick<LighthouseData, "sectors"> | null | undefined): Sector[] => {
    const darkSectorCount = lighthouseData?.sectors.filter((sector) => sector.color === SectorColor.DARK).length ?? 0;
    if (darkSectorCount === 0) return lighthouseData?.sectors ?? [];

    let index = getFirstDarkSectorIndex(lighthouseData);

    if (lighthouseData?.sectors[index].color === SectorColor.DARK) index++;
    return lighthouseData?.sectors.slice(index).concat(lighthouseData?.sectors.slice(0, index)) ?? [];
};

/**
 * The sectors in a snapshot of shiftSectors' order (globalids), taken when editing starts, so
 * lists do not reorder while the user edits. Sectors gone since are dropped; a sector added since
 * goes after the sector before it in the array, i.e. where the user added it. Without a usable
 * snapshot this is shiftSectors.
 */
export const applySectorOrder = (
    lighthouseData: Pick<LighthouseData, "sectors"> | null | undefined,
    order: string[] | null | undefined
) => {
    const sectors = lighthouseData?.sectors ?? [];
    const ordered: Sector[] = [];
    for (const globalid of order ?? []) {
        const sector = sectors.find((s) => s.globalid === globalid && !ordered.includes(s));
        if (sector) ordered.push(sector);
    }
    if (ordered.length === 0) return shiftSectors(lighthouseData);

    for (const [index, sector] of sectors.entries()) {
        if (ordered.includes(sector)) continue;
        let position = -1;
        for (let step = 1; step < sectors.length && position === -1; step++) {
            position = ordered.indexOf(sectors[(index - step + sectors.length) % sectors.length]);
        }
        ordered.splice(position + 1, 0, sector);
    }
    return ordered;
};

/** Sector numbers by globalid for sectors in numbering order: lit sectors from 1, dark ones get none. */
export const getSectorNumbers = (orderedSectors: Sector[]) => {
    const numbers = new Map<string, number>();
    for (const sector of orderedSectors) {
        if (sector.color !== SectorColor.DARK) numbers.set(sector.globalid, numbers.size + 1);
    }
    return numbers;
};
